import { getDocFromServer, updateDoc, setDoc } from 'firebase/firestore'
import { parseAeriesFile } from '@/composables/scheduleImport/aeriesCsvAdapter.js'
import {
  buildAeries504Diff,
  detect504ColumnMapping,
  pivotTo504Roster
} from '@/utils/aeries504AccommodationDiff.js'
import { summarize504Import } from '@/utils/aeries504Review.js'
import {
  buildImportOutcome,
  importActivityPatch,
  describeImportActivity
} from '@/utils/studentImportOutcome.js'
import { splitSettingsPatch } from '@/utils/schoolSettings.js'
import {
  createFixtureSession,
  fixtureSessionBlockReason,
  withFixtureSession,
  DISTRICT_SMOKE_PROFILES
} from '../fixtureSession.js'
import { assertReferralAccess } from '../referralAccessPlan.js'

const check = (condition, message) => {
  if (!condition) throw new Error(message)
}
const values = (data) => ({
  instruction: data.app.accommodations.instruction,
  assessment: data.app.accommodations.assessment,
  plan: data.app.studentData.plan,
  caseManagerId: data.app.studentData.caseManagerId,
  reviewDate: data.app.dates.reviewDate,
  meetingDate: data.app.dates.meetingDate,
  reevalDate: data.app.dates.reevalDate
})

export default {
  id: 'importWorkflow',
  label: '504 import, saved outcome and Undo',
  description:
    'Real CSV parser and import/Undo writers on disposable students; verifies review choices, school isolation, saved outcomes and denied-write reporting. UI checklist below covers browser interactions.',
  destructive: true,
  oncePerSweep: true,
  async run(ctx, step) {
    const reason = fixtureSessionBlockReason(ctx)
    if (reason) {
      step.block('import fixtures', reason)
      return
    }
    const { fetchStudentsBySsid } = await import('@/composables/importShared.js')
    const {
      runAeries504Import,
      undoAeries504Import,
      saveAeries504UndoBatch,
      loadAeries504UndoBatch,
      clearAeries504UndoBatch
    } = await import('@/composables/aeries504AccommodationImport.js')
    const io = createFixtureSession(ctx)
    let student, otherSchoolStudent, baseline, diff, decisions, importedAt, result
    await withFixtureSession(ctx, step, io, async () => {
      await step('prepare a synthetic 504 and a same-SSID student at another campus', async () => {
        const ssid = `SMOKE-${io.schools[0].id}`
        baseline = {
          app: {
            studentData: {
              firstName: 'SMOKE',
              lastName: 'Import',
              ssid,
              plan: '504',
              planStatus: '',
              caseManagerId: ctx.user.uid,
              primaryDisability: ''
            },
            accommodations: {
              instruction: 'Original classroom accommodation',
              assessment: 'Keep original assessment'
            },
            dates: {
              reviewDate: '2026-09-01',
              meetingDate: '2026-09-15',
              reevalDate: '2028-09-01'
            },
            flags: { flag1: false, flag2: false, separateSetting: false },
            schedule: { periods: {} }
          }
        }
        student = await io.student(0, baseline)
        otherSchoolStudent = await io.student(1, baseline)
        const future = `${new Date().getUTCFullYear() + 1}-06-01`
        const csv =
          'State Student ID,Last Name,First Name,Next Review Date,Area of Need,Accommodation Code,Accommodation Detail\n' +
          `${ssid},Import,SMOKE,${future},ATT,O1,Preferential seating near the teacher\n` +
          `${ssid},Import,SMOKE,${future},TST,O2,Extra time on tests\n` +
          `SMOKE-UNMATCHED-${io.schools[0].id},Unmatched,SMOKE,${future},ATT,O1,Do not create a student\n`
        const parsed = await parseAeriesFile(
          new File([csv], 'synthetic-504-smoke.csv', { type: 'text/csv' })
        )
        const { mapping } = detect504ColumnMapping(parsed.headers)
        const roster = pivotTo504Roster(parsed.rows, mapping)
        const studentMap = await fetchStudentsBySsid(Object.keys(roster.students), io.schools[0].id)
        diff = buildAeries504Diff({ students: roster.students, studentMap })
        check(
          diff.students.length === 1 && diff.unmatched.length === 1 && !diff.blocked.length,
          'Synthetic CSV did not resolve to one eligible student and one unmatched row'
        )
        decisions = {
          [`${ssid}|instruction`]: { decision: 'accept' },
          [`${ssid}|assessment`]: { decision: 'keep' }
        }
        const summary = summarize504Import(diff, decisions)
        check(
          summary.students === 1 && summary.replaced >= 1 && summary.kept >= 1,
          'Confirmation summary did not reflect replace and keep choices'
        )
        return 'Real parser and school-scoped SSID lookup; preview contains one write'
      })
      await step('import approved changes and reopen from the server', async () => {
        result = await runAeries504Import({ diff, decisions })
        check(
          result.updated === 1 && result.errors.length === 0 && result.writtenIds[0] === student.id,
          'Synthetic import did not complete exactly one write'
        )
        const data = (await getDocFromServer(student)).data()
        check(
          data.app.accommodations.instruction.includes('Preferential seating'),
          'Approved instruction was not saved'
        )
        check(
          data.app.accommodations.assessment === baseline.app.accommodations.assessment,
          'Keep choice was overwritten'
        )
        check(
          data.app.studentData.plan === '504' &&
            data.app.studentData.caseManagerId === ctx.user.uid &&
            data.app.dates.meetingDate === baseline.app.dates.meetingDate &&
            data.app.dates.reevalDate === baseline.app.dates.reevalDate,
          'Import changed an unrelated plan, owner or date'
        )
        check(
          JSON.stringify(values((await getDocFromServer(otherSchoolStudent)).data())) ===
            JSON.stringify(values(baseline)),
          'Same-SSID student at the other campus changed'
        )
        return 'Accepted and kept values verified; other campus unchanged'
      })
      await step('persist import outcome and reload the school’s Undo batch', async () => {
        const outcome = buildImportOutcome('aeries-504', result)
        importedAt = outcome.finishedAt
        const patch = importActivityPatch('aeries-504', outcome, 'SMOKE')
        const buckets = splitSettingsPatch(patch)
        check(
          Object.keys(buckets.global).length === 0,
          'Import activity was routed to district settings'
        )
        await setDoc(io.schools[0], buckets.school, { merge: true })
        await saveAeries504UndoBatch({
          schoolId: io.schools[0].id,
          importedAt,
          importedBy: 'SMOKE',
          writtenIds: result.writtenIds
        })
        const saved = (await getDocFromServer(io.schools[0])).data()
        check(
          describeImportActivity(saved, 'aeries-504').label === 'Imported successfully',
          'Saved success status was not reloaded'
        )
        check(
          describeImportActivity((await getDocFromServer(io.schools[1])).data(), 'aeries-504')
            .label === 'No import recorded',
          'Another school inherited this import result'
        )
        const undo = await loadAeries504UndoBatch(io.schools[0].id)
        check(
          undo?.writtenIds?.length === 1 && undo.writtenIds[0] === student.id,
          'Reloaded Undo batch differs from the imported student'
        )
        return 'Server reads confirm school-only status and reloadable Undo'
      })
      await step(
        'Undo from the saved batch and verify original values and restored status',
        async () => {
          const batch = await loadAeries504UndoBatch(io.schools[0].id)
          const restored = await undoAeries504Import(batch.writtenIds)
          check(restored.restored === 1 && restored.errors.length === 0, 'Undo failed')
          check(
            JSON.stringify(values((await getDocFromServer(student)).data())) ===
              JSON.stringify(values(baseline)),
            'Undo did not restore the original values'
          )
          await clearAeries504UndoBatch(io.schools[0].id)
          await updateDoc(
            io.schools[0],
            importActivityPatch('aeries-504', {
              status: 'restored',
              restored: 1,
              issues: 0,
              finishedAt: new Date().toISOString()
            })
          )
          const saved = (await getDocFromServer(io.schools[0])).data()
          check(
            saved.lastAeries504ImportAt === importedAt &&
              describeImportActivity(saved, 'aeries-504').label === 'Previous values restored' &&
              !saved.aeries504Undo,
            'Restored status, original import timestamp or Undo clearing is incorrect'
          )
          return 'Original values restored; import timestamp preserved; Undo batch cleared'
        }
      )
      await step(
        'a district coordinator’s denied import reports issues and saves no student changes',
        async () => {
          await io.as(DISTRICT_SMOKE_PROFILES[1])
          await assertReferralAccess(false, () => getDocFromServer(student))
          const denied = await runAeries504Import({ diff, decisions })
          check(
            denied.updated === 0 &&
              denied.errors.length > 0 &&
              denied.errors.every((error) => /permission|insufficient/i.test(error)),
            'Denied import did not return the expected permission failures'
          )
          const patch = importActivityPatch(
            'aeries-504',
            buildImportOutcome('aeries-504', denied),
            'SMOKE'
          )
          await setDoc(io.schools[0], patch, { merge: true })
          const saved = (await getDocFromServer(io.schools[0])).data()
          check(
            describeImportActivity(saved, 'aeries-504').label === 'Imported with issues',
            'Denied import status was not persisted honestly'
          )
          await io.as(DISTRICT_SMOKE_PROFILES[0])
          check(
            JSON.stringify(values((await getDocFromServer(student)).data())) ===
              JSON.stringify(values(baseline)),
            'Denied import changed the synthetic student'
          )
          return 'Real permission failure produces a saved issues outcome with zero student updates'
        }
      )
      step.skip(
        'browser interactions',
        'Automated data checks do not click the wizard. Complete and save the manual import UI checklist below.'
      )
    })
  }
}
